import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  AssignmentListModel2,
  ServiceProviderObservationTypeModel,
} from '../../../../../api-service/clients';
import { moduleClientFactory } from '../../../../../api-service/module-client-factory';
import { ObservationDialog } from './observation-dialog';
import { createObservation } from './observation-api';

const serviceProviderClient =
  moduleClientFactory.createCitizenServiceProviderClient(true);
const assignmentClient =
  moduleClientFactory.createCitizenAssignmentClient(true);

interface RegisterObservationFromEventDialogProps {
  citizenId: string;
  serviceProviderUnitId?: string;
  observationTypeId: string;
  /** The completed event's start date — pre-fills the observation time. */
  eventStartDate: Date;
  currentUserId?: string;
  onClose: () => void;
  onCreated: () => void;
}

type LoadState = 'loading' | 'error' | 'ready';

export function RegisterObservationFromEventDialog({
  citizenId,
  serviceProviderUnitId,
  observationTypeId,
  eventStartDate,
  currentUserId,
  onClose,
  onCreated,
}: RegisterObservationFromEventDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [observationType, setObservationType] =
    useState<ServiceProviderObservationTypeModel | null>(null);
  const [assignments, setAssignments] = useState<AssignmentListModel2[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');

    Promise.all([
      serviceProviderClient.getObservationType({ observationTypeId }),
      serviceProviderUnitId
        ? assignmentClient.getServiceProviderObjectiveInfo({
            citizenId: { value: citizenId },
            serviceProviderUnitId: { value: serviceProviderUnitId },
          })
        : Promise.resolve({ assignments: [] }),
    ])
      .then(([typeResponse, objectiveInfo]) => {
        if (cancelled) return;
        if (!typeResponse.observationType) {
          setLoadState('error');
          return;
        }
        setObservationType(typeResponse.observationType);
        setAssignments(objectiveInfo.assignments ?? []);
        setLoadState('ready');
      })
      .catch(() => {
        if (!cancelled) setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [citizenId, serviceProviderUnitId, observationTypeId]);

  function handleSave(values: {
    assignmentId?: string;
    observedOn: Date;
    observedByUserId: string;
    numericValue?: number;
    booleanValue?: boolean;
    textValue?: string;
    reflection?: string;
  }) {
    if (!serviceProviderUnitId) return;
    createObservation({
      citizenId,
      assignmentId: values.assignmentId,
      serviceProviderUnitId,
      observationTypeId,
      observedOn: values.observedOn,
      observedByUserId: values.observedByUserId,
      numericValue: values.numericValue,
      booleanValue: values.booleanValue,
      textValue: values.textValue,
      reflection: values.reflection,
    })
      .then(() => {
        enqueueSnackbar('Observation oprettet', { variant: 'success' });
        onCreated();
        onClose();
      })
      .catch(() =>
        enqueueSnackbar('Kunne ikke oprette observation', { variant: 'error' })
      );
  }

  if (loadState === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={120}
        p={4}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadState === 'error' || !observationType) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Kunne ikke hente observationstype. Luk og prøv igen.
      </Alert>
    );
  }

  return (
    <ObservationDialog
      open
      isEdit={false}
      type={observationType}
      assignments={assignments}
      initialObservedOn={eventStartDate}
      currentUserId={currentUserId}
      onClose={onClose}
      onSave={handleSave}
    />
  );
}
